/**
 * CountUp — animated number that tickers from 0 (or `from`) to `to`
 * over a short duration, with a one-shot **gold sparkle** that fires
 * when a "milestone" is reached (multiples of 100, or any custom
 * threshold the caller passes).  Designed for CockpitHero stats,
 * Diploks balances, leaderboard scores, etc.
 *
 * Respects ``prefers-reduced-motion`` — sets the final value
 * immediately and skips both the count animation and the sparkle.
 *
 * Usage:
 *   <CountUp to={1432} duration={1.4} />
 *   <CountUp to={250} prefix="₹" milestoneEvery={50} />
 *   <CountUp to={9.4} decimals={1} suffix=" /10" />
 *
 * Sparkle policy:
 *   - Fires once when the animation completes IF the final value is
 *     a multiple of `milestoneEvery` (default 100) OR ≥ `milestoneMin`.
 *   - Renders a small `Sparkles` icon that pulses gold, then fades.
 *   - Setting `sparkle={false}` disables it entirely.
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function CountUp({
  to = 0,
  from = 0,
  duration = 1.2,
  decimals = 0,
  prefix = "",
  suffix = "",
  className = "",
  sparkle = true,
  milestoneEvery = 100,
  milestoneMin = 100,
  testid,
}) {
  const safeTo = Number.isFinite(to) ? Number(to) : 0;
  const safeFrom = Number.isFinite(from) ? Number(from) : 0;
  const reduced = prefersReducedMotion();

  const [value, setValue] = useState(reduced ? safeTo : safeFrom);
  const [showSparkle, setShowSparkle] = useState(false);
  const rafRef = useRef(0);
  const startTsRef = useRef(0);

  useEffect(() => {
    if (reduced) {
      setValue(safeTo);
      return undefined;
    }
    // Cancel any in-flight tween so successive re-renders don't fight.
    cancelAnimationFrame(rafRef.current);
    startTsRef.current = 0;

    const step = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts;
      const elapsed = (ts - startTsRef.current) / 1000;
      const t = Math.min(1, elapsed / Math.max(0.05, duration));
      const eased = easeOutCubic(t);
      const next = safeFrom + (safeTo - safeFrom) * eased;
      setValue(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        // Done — fire the sparkle if eligible.
        if (
          sparkle
          && safeTo >= milestoneMin
          && (milestoneEvery <= 0 || Math.round(safeTo) % milestoneEvery === 0)
        ) {
          setShowSparkle(true);
          setTimeout(() => setShowSparkle(false), 1500);
        }
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [safeTo, safeFrom, duration, reduced, sparkle, milestoneEvery, milestoneMin]);

  const display = decimals > 0
    ? value.toFixed(decimals)
    : Math.round(value).toLocaleString();

  return (
    <span className={`relative inline-flex items-baseline ${className}`} data-testid={testid}>
      <span className="tabular-nums">
        {prefix}{display}{suffix}
      </span>
      {showSparkle ? (
        <span
          aria-hidden
          className="absolute -top-1 -right-3 sm:-right-4 pointer-events-none"
          data-testid={testid ? `${testid}-sparkle` : "countup-sparkle"}
        >
          <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-brand-gold animate-countup-sparkle" />
        </span>
      ) : null}
    </span>
  );
}
